import { memo, useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Image } from '@amplience/content-types/typings/c-header';
import type { NavigationBar as NavigationBarSchema } from '@amplience/content-types/typings/c-navigation-bar';
import type { PromotionalLinksNav as PromotionalLinksNavProps } from '@amplience/content-types/typings/c-promotional-links-nav';
import type { SubNav as SubNavProps } from '@amplience/content-types/typings/c-sub-nav';
import { useCartContext } from '../../../context/cartContext';
import { useLocaleContext } from '../../../context/localeContext';
import { useOverlayContext } from '../../../context/overlayContext';
import { useDidMount } from '../../../hooks/useDidMount';
import { useEscapeKeyListener } from '../../../hooks/useEscapeKeyListener';
import { useMediaMatch } from '../../../hooks/useMediaMatch';
import { Account, Close, Hamburger, Search } from '../../../icons';
import { ImagePartial } from '../../../partials/image';
import { media } from '../../../stylings';
import type { AmplienceContextProps } from '../../../types/amplience';
import { getBasketPageLink } from '../../../utils/cartUtils';
import { Locale } from '../../../utils/internationalization/localeHelper';
import { showProgressBar } from '../../../utils/progressBarStatus';
import { CartIcon } from '../../cart/cartIcon';
import type { CheckoutCtaMenuProps } from '../../cart/checkoutCtaMenu/CheckoutCtaMenu';
import type { EmptyBasketProps } from '../../cart/emptyBasket';
import type { MiniCartSliderProps } from '../../cart/miniCart/miniCartSlider/MiniCartSliderProps';
import { HeaderNavigation } from '../../navigation/headerNavigation/HeaderNavigation';
import type { DesktopSearchBarProps } from '../desktopSearchBar/DesktopSearchBar';
import type { MobileHamburgerMenuProps } from '../mobileHamburgerMenu/MobileHamburgerMenu';
import { SearchBar } from '../searchBar/SearchBar';
import {
  Cart,
  CartIconWrapper,
  HamburgerButton,
  IconsWrapper,
  Logo,
  MyAccount,
  MyAccountHoverBackground,
  NavBarWrapperContainer,
  Placeholder,
  SearchIcon,
  SearchOverlay,
  StickyWrapper,
  Wrapper,
} from './NavigationBar.styles';

/* eslint-disable promise/prefer-await-to-then */
const MiniCartSlider = dynamic<MiniCartSliderProps>(
  () =>
    import(/* webpackChunkName: "MiniCartSlider" */ '../../cart/miniCart/miniCartSlider/MiniCartSlider').then(
      (mod) => mod.MiniCartSlider,
    ),
  {
    ssr: false,
  },
);
const MobileHamburgerMenu = dynamic<MobileHamburgerMenuProps>(
  () =>
    import(/* webpackChunkName: "MobileHamburgerMenu" */ '../mobileHamburgerMenu/MobileHamburgerMenu').then(
      (mod) => mod.MobileHamburgerMenu,
    ),
  {
    ssr: false,
  },
);
const CheckoutCtaMenu = dynamic<CheckoutCtaMenuProps>(
  () =>
    import(/* webpackChunkName: "CheckoutCtaMenu" */ '../../cart/checkoutCtaMenu/CheckoutCtaMenu').then(
      (mod) => mod.CheckoutCtaMenu,
    ),
  {
    ssr: false,
  },
);
const MobileSearchBar = dynamic<Record<string, never>>(
  () =>
    import(/* webpackChunkName: "MobileSearchBar" */ '../mobileSearchBar/MobileSearchBar').then(
      (mod) => mod.MobileSearchBar,
    ),
  {
    ssr: false,
  },
);
const DesktopSearchBar = dynamic<DesktopSearchBarProps>(
  () =>
    import(/* webpackChunkName: "DesktopSearchBar" */ '../desktopSearchBar/DesktopSearchBar').then(
      (mod) => mod.DesktopSearchBar,
    ),
  {
    ssr: false,
  },
);
/* eslint-enable promise/prefer-await-to-then */

export type NavigationBarProps = NavigationBarSchema &
  AmplienceContextProps & {
    subNavData?: SubNavProps;
    promotionalLinksNavData?: PromotionalLinksNavProps;
    emptyBasketComponent?: EmptyBasketProps;
    headerLogo?: Image;
  };

function SearchSection() {
  const { t } = useTranslation(['lib-global-common']);
  const { isSearchOverlayOpen, setIsSearchOverlayOpen } = useOverlayContext();

  const handleSearchIconClick = useCallback(() => {
    setIsSearchOverlayOpen((prev) => !prev);
  }, [setIsSearchOverlayOpen]);

  return (
    <>
      <SearchBar forDesktop idPrefix="desktop" />
      <SearchIcon
        data-testid="mobileSearchButton"
        onClick={handleSearchIconClick}
        aria-label={isSearchOverlayOpen ? t('hamburger.menu.close') : t('search.placeholder')}
      >
        {isSearchOverlayOpen ? <Close viewBox="-10 -4 40 25" aria-hidden alt="" /> : <Search aria-hidden alt="" />}
      </SearchIcon>
    </>
  );
}

const SearchSectionMemo = memo(SearchSection);

export function NavigationBar({
  context,
  mobileOnlyFeaturedCards,
  subNavData,
  promotionalLinksNavData,
  emptyBasketComponent,
  headerLogo,
}: NavigationBarProps) {
  const { t } = useTranslation('lib-global-common');
  const { hierarchyData } = context ?? { hierarchyData: [] };
  const { locale } = useLocaleContext();
  const { checkoutSecurely, itemsInCart } = useCartContext();
  const didMount = useDidMount();

  const {
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isMiniCartOpen,
    isSearchOverlayOpen,
    setIsSearchOverlayOpen,
    isMarketingMessageInView,
    setIsMiniCartOpen,
  } = useOverlayContext();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const router = useRouter();
  const routerAsPath = router.asPath.split('?')[0];
  const [sliderOffset, setSliderOffset] = useState(0);

  const handleCloseSearchOverLay = useCallback(() => {
    setIsSearchOverlayOpen(false);
  }, [setIsSearchOverlayOpen]);

  useEffect(() => {
    let lastScrollTop = 0;

    const onScroll = () => {
      const currentScrollTop = window.scrollY;
      const placeholderTop = placeholderRef.current?.offsetTop || 0;
      const wrapperHeight = wrapperRef.current?.clientHeight || 0;
      const headerTop = Math.max(placeholderTop - wrapperHeight, 1);

      if (document.documentElement.scrollTop >= Math.max(headerTop, placeholderTop) + wrapperHeight) {
        placeholderRef.current?.classList.add('sticky');
        wrapperRef.current?.classList.add('sticky');
      } else if (document.documentElement.scrollTop <= Math.max(headerTop, placeholderTop)) {
        placeholderRef.current?.classList.remove('sticky');
        wrapperRef.current?.classList.remove('sticky');
      }

      if (lastScrollTop > currentScrollTop) {
        wrapperRef.current?.classList.add('show-navbar');
        wrapperRef.current?.classList.remove('hide-navbar');
      } else {
        wrapperRef.current?.classList.add('hide-navbar');
        wrapperRef.current?.classList.remove('show-navbar');
      }

      lastScrollTop = currentScrollTop;
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMiniCartOpen(false);
  }, [routerAsPath, setIsMiniCartOpen]);

  useEffect(() => {
    const notificationBarHeight = document.getElementById('clarks-notification-bar')?.offsetHeight ?? 0;
    const searchBarHeight = document.getElementById('nav-bar-sticky-wrapper')?.offsetHeight ?? 0;
    const searchModalHeight = document.getElementById('search-modal-container')?.offsetHeight ?? 0;
    const headerHeight = document.getElementById('clarks-header')?.offsetHeight ?? 0;
    const marketingMessageHeight = document.getElementById('marketing-message')?.offsetHeight ?? 0;
    const marketingMessageInView = isMarketingMessageInView ? 79 + marketingMessageHeight : 49;

    let sliderOffsetValue: number;

    if (!isDesktop) {
      sliderOffsetValue = isSearchOverlayOpen ? searchBarHeight + notificationBarHeight : marketingMessageInView;
    } else {
      sliderOffsetValue = isSearchOverlayOpen ? searchModalHeight + notificationBarHeight : headerHeight;
    }

    setSliderOffset(sliderOffsetValue);
  }, [isSearchOverlayOpen, isDesktop, isMarketingMessageInView]);

  useEffect(() => {
    const searchModalInputElement: HTMLInputElement | null = document.querySelector('.aa-Input');
    const handleEscapeKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchModalInputElement?.value === '') {
        setIsSearchOverlayOpen(false);
      }
    };

    if (isSearchOverlayOpen) {
      searchModalInputElement?.addEventListener('keydown', handleEscapeKeyPress);
    }

    return () => searchModalInputElement?.removeEventListener('keydown', handleEscapeKeyPress);
  }, [setIsSearchOverlayOpen, isSearchOverlayOpen]);

  const handleOnMyAccountClick = useCallback(() => {
    showProgressBar();
    sessionStorage.setItem('lastLocation', '/my-account');
  }, []);

  const handleCloseCartKeydown = useCallback(async (): Promise<void> => {
    setIsMiniCartOpen(false);
  }, [setIsMiniCartOpen]);

  useEscapeKeyListener(handleCloseCartKeydown);

  const openMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, [setIsMobileMenuOpen]);

  return (
    <>
      <Placeholder ref={placeholderRef} />
      <StickyWrapper ref={wrapperRef} id="nav-bar-sticky-wrapper">
        <NavBarWrapperContainer>
          {isDesktop && isSearchOverlayOpen && <DesktopSearchBar headerLogo={headerLogo} />}
          <Wrapper data-testid="navBarWrapper">
            <Logo data-testid="logoComponent">
              <Link href="/" locale={locale} passHref data-testid="logoLink">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <a aria-label={t('header.link.home')} onClick={handleCloseSearchOverLay}>
                  <ImagePartial
                    altText={headerLogo?.altText}
                    src={headerLogo?.image?.name ?? ''}
                    assetType={headerLogo?.assetType}
                    useOriginalSize
                    defaultHost={headerLogo?.image?.defaultHost}
                    data-testid="logoImage"
                  />
                </a>
              </Link>
            </Logo>

            {hierarchyData && <HeaderNavigation hierarchyData={hierarchyData} />}

            <IconsWrapper>
              <SearchSectionMemo />
              <Link href="/my-account" passHref locale={locale}>
                <MyAccount
                  onClick={handleOnMyAccountClick}
                  aria-label={t('header.link.my.account')}
                  data-testid="myAccountIcon"
                >
                  <MyAccountHoverBackground className="hover-bg" />
                  <Account aria-hidden />
                </MyAccount>
              </Link>
              <CartIconWrapper>
                <Link
                  href="/cart"
                  as={`/${getBasketPageLink(locale)}`}
                  passHref
                  locale={locale === Locale.STOREFRONT_OUTLET_LOCALE ? false : locale}
                >
                  <Cart aria-label={t('header.link.cart')}>
                    <CartIcon itemsInCart={itemsInCart} aria-hidden />
                  </Cart>
                </Link>
              </CartIconWrapper>
              {didMount && !isDesktop && (
                <HamburgerButton
                  data-testid="mobileHamburgerButton"
                  type="button"
                  aria-label={t('navigation.bar.hamburger.menu')}
                  onClick={openMobileMenu}
                >
                  <Hamburger aria-hidden />
                </HamburgerButton>
              )}
            </IconsWrapper>
          </Wrapper>
        </NavBarWrapperContainer>

        {!isDesktop && isSearchOverlayOpen && <MobileSearchBar />}
      </StickyWrapper>
      {isSearchOverlayOpen && <SearchOverlay onClick={handleCloseSearchOverLay} />}
      {isMobileMenuOpen && (
        <MobileHamburgerMenu
          mobileOnlyFeaturedCards={mobileOnlyFeaturedCards}
          setMobileMenuState={setIsMobileMenuOpen}
          subNavData={subNavData}
          promotionalLinksNavData={promotionalLinksNavData}
          hierarchyData={hierarchyData}
        />
      )}
      {isMiniCartOpen && (
        <MiniCartSlider
          emptyBasketComponent={emptyBasketComponent}
          scrollTop={document.documentElement.scrollTop}
          sliderOffset={sliderOffset}
          isDesktop={isDesktop}
        />
      )}
      {!isMiniCartOpen && checkoutSecurely && <CheckoutCtaMenu sliderOffset={sliderOffset} />}
    </>
  );
}
